import {
  Children,
  Fragment,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import styled from "styled-components";

import { parseRichLabel } from "./richLabel";

export type TextVariantName = string;

export type TextVariantDefinition = {
  /** Element rendered when this definition is passed to `<Text>` or a consumer wrapper. */
  as?: ElementType;
  /** Optional inline style injected by a consuming app or wrapper. */
  style?: CSSProperties;
};

/** Map of text variant names to their style and semantic element definition. */
export type TextVariantMap = Readonly<Record<string, TextVariantDefinition>>;

/**
 * 마크업 태그 이름 → 해당 구간에 입힐 variant definition 맵. (`Text.labelStyle`)
 *
 * 예) `{ emphasis: { style: { color: "var(--c6-color-text-accent)" } } }` 와
 * 함께 `"Camera: <emphasis>123</emphasis>"`를 넘기면 `123`만 강조 렌더된다.
 */
export type TextLabelStyle = Readonly<Record<string, TextVariantDefinition>>;

/** String key union inferred from a concrete text variant map. */
export type TextVariantKey<Variants extends TextVariantMap> = Extract<keyof Variants, string>;

export function defineTextVariants<const Variants extends TextVariantMap>(
  variants: Variants,
): Variants {
  return variants;
}

const weight = {
  regular: "var(--c6-typography-weight-regular)",
  bold: "var(--c6-typography-weight-bold)",
};
const size = {
  caption: "var(--c6-typography-size-caption)",
  body: "var(--c6-typography-size-body)",
  title: "var(--c6-typography-size-title)",
  head: "var(--c6-typography-size-head)",
  headLarge: "var(--c6-typography-size-head-large)",
  headXLarge: "var(--c6-typography-size-head-x-large)",
};
const leading = {
  tight: "var(--c6-typography-line-height-tight)",
  snug: "var(--c6-typography-line-height-snug)",
  normal: "var(--c6-typography-line-height-normal)",
};

// 역할 기반 레퍼런스 스케일 — size/lineHeight/weight 모두 theme typography 토큰(CSS 변수) 참조.
// 값 근거: sigmakit 사용 실측 + dense 엔터프라이즈 표준(caption 12 / body 14 / title 16 / head 20px).
// Text는 headless라 이건 BYO 안 한 소비처용 기본값 — 소비처는 자체 variant map을 주입할 수 있다.
export const defaultTextVariants = defineTextVariants({
  "head-normal": {
    as: "h1",
    style: { fontSize: size.head, lineHeight: leading.tight, fontWeight: weight.regular },
  },
  "head-bold": {
    as: "h1",
    style: { fontSize: size.head, lineHeight: leading.tight, fontWeight: weight.bold },
  },
  "head-large-normal": {
    as: "h1",
    style: { fontSize: size.headLarge, lineHeight: leading.tight, fontWeight: weight.regular },
  },
  "head-large-bold": {
    as: "h1",
    style: { fontSize: size.headLarge, lineHeight: leading.tight, fontWeight: weight.bold },
  },
  "head-xlarge-normal": {
    as: "h1",
    style: { fontSize: size.headXLarge, lineHeight: leading.tight, fontWeight: weight.regular },
  },
  "head-xlarge-bold": {
    as: "h1",
    style: { fontSize: size.headXLarge, lineHeight: leading.tight, fontWeight: weight.bold },
  },
  "title-normal": {
    as: "h2",
    style: { fontSize: size.title, lineHeight: leading.tight, fontWeight: weight.regular },
  },
  "title-bold": {
    as: "h2",
    style: { fontSize: size.title, lineHeight: leading.tight, fontWeight: weight.bold },
  },
  "body-normal": {
    as: "p",
    style: { fontSize: size.body, lineHeight: leading.normal, fontWeight: weight.regular },
  },
  "body-bold": {
    as: "p",
    style: { fontSize: size.body, lineHeight: leading.normal, fontWeight: weight.bold },
  },
  "caption-normal": {
    as: "span",
    style: { fontSize: size.caption, lineHeight: leading.snug, fontWeight: weight.regular },
  },
  "caption-bold": {
    as: "span",
    style: { fontSize: size.caption, lineHeight: leading.snug, fontWeight: weight.bold },
  },
});

/** Variant names included in the default Carbon6 text theme. */
export type DefaultTextVariant = TextVariantKey<typeof defaultTextVariants>;

export interface TextProps extends HTMLAttributes<HTMLElement> {
  variant?: TextVariantDefinition;
  ignorePointer?: boolean;
  /**
   * 마크업 태그(`<tag>…</tag>`) 이름 → 구간별 variant 맵. 지정하면 문자열
   * children을 파싱해 매칭된 태그 구간만 그 variant로 감싼 span으로 강조한다.
   * 평문·매핑되지 않은 태그·비문자열 children(예: 마커 element)은 그대로 둔다.
   */
  labelStyle?: TextLabelStyle;
}

const StyledText = styled.span<{ $ignorePointer?: boolean }>`
  min-width: 0;
  margin: 0;
  color: ${({ theme }) => theme.color.text.default};
  font-family: inherit;
  letter-spacing: 0;
  overflow-wrap: anywhere;

  ${({ $ignorePointer }) => ($ignorePointer ? "pointer-events: none;" : "")}
`;

const emptyTextVariant: TextVariantDefinition = {};

export function Text({
  variant,
  className,
  ignorePointer = false,
  labelStyle,
  children,
  ...rest
}: TextProps) {
  const nullableVariant = variant as TextProps["variant"] | null;
  if (nullableVariant === null) {
    throw new TypeError("Text variant cannot be null.");
  }

  const variantDefinition = nullableVariant ?? emptyTextVariant;
  const Component = variantDefinition.as ?? "span";
  const content = labelStyle === undefined ? children : renderLabelChildren(children, labelStyle);

  return (
    <StyledText
      as={Component}
      $ignorePointer={ignorePointer}
      className={className}
      {...rest}
      style={mergeTextStyle(variantDefinition.style, rest.style)}
    >
      {content}
    </StyledText>
  );
}

/**
 * children 중 문자열만 `labelStyle` 마크업으로 파싱해 구간별 variant를 입힌다.
 * 비문자열 children(예: required 마커 element)은 그대로 통과시켜 혼합 사용을 허용한다.
 */
function renderLabelChildren(children: ReactNode, labelStyle: TextLabelStyle): ReactNode {
  return Children.map(children, (child) =>
    typeof child === "string" ? renderRichSegments(child, labelStyle) : child,
  );
}

function renderRichSegments(source: string, labelStyle: TextLabelStyle): ReactNode {
  return parseRichLabel(source).map((segment, index) => {
    const definition = segment.tag === null ? undefined : labelStyle[segment.tag];
    // 평문이거나 매핑되지 않은 태그는 스타일 없이 텍스트만 — 마크업이 노출되지 않게 한다.
    if (definition === undefined) {
      return <Fragment key={index}>{segment.text}</Fragment>;
    }
    const SegmentTag = definition.as ?? "span";
    return (
      <SegmentTag key={index} style={definition.style}>
        {segment.text}
      </SegmentTag>
    );
  });
}

function mergeTextStyle(
  variantStyle: CSSProperties | undefined,
  style: CSSProperties | undefined,
): CSSProperties | undefined {
  if (variantStyle === undefined) {
    return style;
  }
  if (style === undefined) {
    return variantStyle;
  }
  return {
    ...variantStyle,
    ...style,
  };
}

Text.displayName = "Text";
